/**
 * EMS controller chip — a small badge that sits ON the site bus, NOT
 * on the energy path. Communicates "this is the controller watching
 * the flow" with a dashed control link drawn separately to the BESS
 * trunk.
 *
 * Compact: vortex glyph + "EMS" pill below + mode label tucked under.
 * Pulses at 1 Hz when the controller is actively tracking.
 */
import type { ControllerMode } from "@/data/types";
import { MODE_CATALOG } from "@/data/modeCatalog";
import { scada } from "../scadaColors";

export function EmsHub({
  cx, cy, r, mode, tracking,
}: {
  cx: number; cy: number; r: number;
  mode: string;
  tracking: boolean;
}) {
  const brand = scada.brand();
  const modeLabel = MODE_CATALOG[mode as ControllerMode]?.label ?? mode;
  return (
    <g style={{ cursor: "pointer" }}>
      {/* Pulse halo — only when the controller is actively tracking */}
      {tracking && (
        <circle
          cx={cx} cy={cy} r={r + 6}
          fill="none"
          stroke={brand} strokeOpacity={0.30} strokeWidth={1}
          strokeDasharray="2 4"
        >
          <animate attributeName="r" values={`${r + 5};${r + 10};${r + 5}`} dur="2.4s" repeatCount="indefinite" />
        </circle>
      )}
      {/* Chip body — opaque so the bus line is hidden behind it */}
      <circle cx={cx} cy={cy} r={r + 3} fill="var(--surface)" />
      <circle cx={cx} cy={cy} r={r} fill="var(--surface)" stroke={brand} strokeWidth={1.8} />
      {/* Vortex glyph */}
      <g transform={`translate(${cx} ${cy})`} pointerEvents="none">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
          const a = (i / 8) * Math.PI * 2;
          const inner = r * 0.22;
          const outer = r * 0.62;
          return (
            <line key={i}
              x1={Math.cos(a) * inner} y1={Math.sin(a) * inner}
              x2={Math.cos(a) * outer} y2={Math.sin(a) * outer}
              stroke={brand}
              strokeOpacity={0.55 + (i % 2) * 0.30}
              strokeWidth={1.5}
              strokeLinecap="round"
            />
          );
        })}
        <circle cx={0} cy={0} r={r * 0.18} fill={brand} />
      </g>
      {/* "EMS" label below the chip */}
      <text x={cx} y={cy + r + 12} textAnchor="middle"
        fontSize={9} fontWeight={800} fill={brand} letterSpacing={1.4}>
        EMS
      </text>
      {/* Mode tag + tracking pip on a single line */}
      <text x={cx} y={cy + r + 24} textAnchor="middle"
        fontSize={8} fill="var(--text-muted)">
        {modeLabel}{tracking ? " · ●" : ""}
      </text>
    </g>
  );
}
